// Development debug panel - shows in local and staging environments
import { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { config } from '../../config/app';
import './DevDebugPanel.css';

interface DebugInfo {
  database: {
    url: string;
    type: 'local' | 'production';
  };
  email: {
    provider: 'resend' | 'inbucket';
    url?: string;
  };
  stripe: {
    mode: 'test' | 'live';
    priceId?: string;
    hasConfig: boolean;
  };
  environment: string;
  baseUrl: string;
  buildInfo: {
    mode: string;
    timestamp: string;
  };
}

export function DevDebugPanel() {
  const { user } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('dev-debug-panel-collapsed');
      return saved ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);

  // Save collapsed state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('dev-debug-panel-collapsed', JSON.stringify(isCollapsed));
    } catch {
      // Silently ignore localStorage errors
    }
  }, [isCollapsed]);

  useEffect(() => {
    // Only show in local and staging environments
    if (import.meta.env.MODE !== 'development' && import.meta.env.MODE !== 'staging') return;

    const info: DebugInfo = {
      database: {
        url: config.supabase.url,
        type: config.supabase.isDevelopment ? 'local' : 'production'
      },
      email: {
        provider: config.supabase.isDevelopment ? 'inbucket' : 'resend',
        url: config.supabase.isDevelopment ? config.auth.emailTesting?.inbucketUrl : undefined
      },
      stripe: {
        mode: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY?.includes('pk_test') ? 'test' : 'live',
        priceId: import.meta.env.VITE_STRIPE_PRICE_ID,
        hasConfig: !!(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY && import.meta.env.VITE_STRIPE_PRICE_ID)
      },
      environment: import.meta.env.MODE,
      baseUrl: config.auth.redirectUrls.base,
      buildInfo: {
        mode: import.meta.env.MODE,
        timestamp: new Date().toISOString()
      }
    };

    setDebugInfo(info);
  }, []);

  // Don't render in production
  if ((import.meta.env.MODE !== 'development' && import.meta.env.MODE !== 'staging') || !debugInfo) {
    return null;
  }

  const formatUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      return `${urlObj.hostname}${urlObj.port ? ':' + urlObj.port : ''}`;
    } catch {
      return url;
    }
  };

  const getEnvironmentDisplayName = (mode: string) => {
    switch (mode) {
      case 'development': return 'Local';
      case 'staging': return 'Staging';
      case 'production': return 'Production';
      default: return mode;
    }
  };

  return (
    <div className={`dev-debug-panel ${isCollapsed ? 'collapsed' : 'expanded'}`}>
      <div className="debug-header" onClick={() => setIsCollapsed(!isCollapsed)}>
        <span className="debug-title">🔧 Dev Info</span>
        <span className="collapse-icon">{isCollapsed ? '▼' : '▲'}</span>
      </div>
      
      {!isCollapsed && (
        <div className="debug-content">
          <div className="debug-section">
            <h4>🗄️ Database</h4>
            <div className="debug-item">
              <span className="label">Type:</span>
              <span className={`value ${debugInfo.database.type}`}>
                {debugInfo.database.type === 'local' ? '🏠 Local' : '☁️ Production'}
              </span>
            </div>
            <div className="debug-item">
              <span className="label">URL:</span>
              <span className="value small">{formatUrl(debugInfo.database.url)}</span>
            </div>
          </div>

          <div className="debug-section">
            <h4>📧 Email</h4>
            <div className="debug-item">
              <span className="label">Provider:</span>
              <span className={`value ${debugInfo.email.provider}`}>
                {debugInfo.email.provider === 'inbucket' ? '📥 Inbucket (Local)' : '📮 Resend (Production)'}
              </span>
            </div>
            {debugInfo.email.url && (
              <div className="debug-item">
                <span className="label">Test URL:</span>
                <a 
                  href={debugInfo.email.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="value link"
                >
                  Open Inbucket →
                </a>
              </div>
            )}
          </div>

          <div className="debug-section">
            <h4>💳 Stripe</h4>
            <div className="debug-item">
              <span className="label">Status:</span>
              <span className={`value ${debugInfo.stripe.hasConfig ? 'configured' : 'missing'}`}>
                {debugInfo.stripe.hasConfig ? '✅ Configured' : '❌ Missing Keys'}
              </span>
            </div>
            {debugInfo.stripe.hasConfig && (
              <>
                <div className="debug-item">
                  <span className="label">Mode:</span>
                  <span className={`value ${debugInfo.stripe.mode}`}>
                    {debugInfo.stripe.mode === 'test' ? '🧪 Test Mode' : '💰 Live Mode'}
                  </span>
                </div>
                {debugInfo.stripe.priceId && (
                  <div className="debug-item">
                    <span className="label">Price ID:</span>
                    <span className="value small">{debugInfo.stripe.priceId.substring(0, 20)}...</span>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="debug-section">
            <h4>🔐 Current User</h4>
            {user ? (
              <>
                <div className="debug-item">
                  <span className="label">Email:</span>
                  <span className="value small">{user.email}</span>
                </div>
                <div className="debug-item">
                  <span className="label">Tier:</span>
                  <span className={`value tier-${user.tier}`}>
                    {user.tier === 'premium' ? '⭐ Premium' : '🆓 Free'}
                  </span>
                </div>
                <div className="debug-item">
                  <span className="label">ID:</span>
                  <span className="value small">{user.uid.substring(0, 8)}...</span>
                </div>
              </>
            ) : (
              <div className="debug-item">
                <span className="value">Not logged in</span>
              </div>
            )}
          </div>

          <div className="debug-section">
            <h4>🌐 Environment</h4>
            <div className="debug-item">
              <span className="label">Mode:</span>
              <span className="value">{getEnvironmentDisplayName(debugInfo.environment)}</span>
            </div>
            <div className="debug-item">
              <span className="label">Base URL:</span>
              <span className="value small">{formatUrl(debugInfo.baseUrl)}</span>
            </div>
            <div className="debug-item">
              <span className="label">Built:</span>
              <span className="value small">{new Date(debugInfo.buildInfo.timestamp).toLocaleTimeString()}</span>
            </div>
          </div>

          <div className="debug-section">
            <h4>🛠️ Quick Actions</h4>
            <div className="debug-actions">
              {debugInfo.email.url && (
                <button 
                  className="debug-action-btn"
                  onClick={() => window.open(debugInfo.email.url, '_blank')}
                >
                  📧 Open Inbucket
                </button>
              )}
              <button 
                className="debug-action-btn"
                onClick={() => {
                  // eslint-disable-next-line no-console
                  console.log('🔧 Debug Info:', debugInfo);
                  // eslint-disable-next-line no-console
                  console.log('🔐 Current User:', user);
                  // eslint-disable-next-line no-console
                  console.log('🌐 Environment Variables:', {
                    VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
                    VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY ? '***' : 'missing',
                    VITE_STRIPE_PUBLISHABLE_KEY: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ? '***' : 'missing',
                    VITE_STRIPE_PRICE_ID: import.meta.env.VITE_STRIPE_PRICE_ID || 'missing'
                  });
                }}
              >
                🖨️ Log Debug Info
              </button>
              <button 
                className="debug-action-btn"
                onClick={() => {
                  localStorage.clear();
                  alert('Local storage cleared! Refresh the page to see changes.');
                }}
              >
                🗑️ Clear Storage
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
